import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useWalletSurfaceAccess } from 'src/pages/Wallet/hooks/useWalletSurfaceAccess';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { useToastContext } from 'src/providers/ToastProvider/Toast.hook';

import { shouldBlockSendWithoutWapiPlan } from './Chat.actions.functions';

import { tWapiPlanSendChannel, tWapiPlanSendGuard } from './useWapiPlanSendGuard.hook';

/**
 * RET-2349 (KTD2) — the one plan guard every chat send producer calls, on the
 * channel it puts in the request. It wraps the existing pure rule
 * `shouldBlockSendWithoutWapiPlan`; it adds no second predicate.
 *
 * Fails open (R3): an unresolved plan or exception never blocks, because
 * `isKnownWithoutWapiAccess` is `true` only with positive evidence. A missing
 * channel never blocks either.
 */
export function useComputedWapiPlanSendGuard(): tWapiPlanSendGuard {
  const { t } = useTranslation(['chat/chat']);
  const { addAlert } = useToastContext();
  const { isKnownWithoutWapiAccess } = useWalletSurfaceAccess();

  const isWapiPlanSendBlocked = useCallback((channel: tWapiPlanSendChannel): boolean =>
    channel !== undefined &&
    channel !== null &&
    shouldBlockSendWithoutWapiPlan({ channel, isKnownWithoutWapiAccess }),
    [isKnownWithoutWapiAccess],
  );

  const blockWapiPlanSend = useCallback((channel: tWapiPlanSendChannel): boolean => {
    const blocked = isWapiPlanSendBlocked(channel);
    if (blocked) {
      addAlert({
        type: eAlertType.ERROR,
        message: t('wapiPlanReadOnly.message'),
      });
    }
    return blocked;
  }, [isWapiPlanSendBlocked, addAlert, t]);

  return useMemo(
    () => ({ blockWapiPlanSend, isWapiPlanSendBlocked }),
    [blockWapiPlanSend, isWapiPlanSendBlocked],
  );
}
